<template>
  <div class="transcode-admin">
    <div class="page-header">
      <h2>视频转码队列</h2>
      <p class="subtitle">管理视频转码任务，查看状态和处理失败任务</p>
    </div>

    <!-- 筛选栏 -->
    <div class="filter-bar">
      <el-select v-model="filterStatus" placeholder="按状态筛选" clearable @change="loadTasks" style="width: 160px">
        <el-option label="全部" :value="null" />
        <el-option label="待处理" :value="0" />
        <el-option label="转码中" :value="1" />
        <el-option label="成功" :value="2" />
        <el-option label="失败" :value="3" />
      </el-select>
      <el-button icon="el-icon-refresh" @click="loadTasks" :loading="loading">刷新</el-button>
      <span class="task-count" v-if="total > 0">共 {{ total }} 条记录</span>
    </div>

    <!-- 任务列表 -->
    <el-table :data="tasks" v-loading="loading" border stripe style="width: 100%">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column prop="sourceFileName" label="文件名" min-width="200" show-overflow-tooltip />
      <el-table-column label="状态" width="140" align="center">
        <template slot-scope="{ row }">
          <div v-if="row.status === 1" style="padding: 0 8px;">
            <el-progress :percentage="row.progress || 0" :stroke-width="14" :text-inside="true" status="" />
          </div>
          <el-tag v-else :type="statusTag(row.status)" size="small" effect="dark">
            {{ statusText(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="businessType" label="业务类型" width="100" />
      <el-table-column label="创建时间" width="170">
        <template slot-scope="{ row }">
          {{ formatTime(row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="完成时间" width="170">
        <template slot-scope="{ row }">
          {{ row.completedTime ? formatTime(row.completedTime) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="errorMessage" label="错误信息" min-width="200" show-overflow-tooltip>
        <template slot-scope="{ row }">
          <span v-if="row.errorMessage" style="color: #f56c6c">{{ row.errorMessage }}</span>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="100" align="center" fixed="right">
        <template slot-scope="{ row }">
          <el-button 
            v-if="row.status === 3" 
            type="text" 
            size="small" 
            icon="el-icon-refresh-right"
            @click="retryTask(row)"
          >重试</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-wrap" v-if="total > pageSize">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="loadTasks"
      />
    </div>
  </div>
</template>

<script>
import axios from 'index/axios'

export default {
  name: 'TranscodeAdmin',
  data() {
    return {
      tasks: [],
      loading: false,
      filterStatus: null,
      currentPage: 1,
      pageSize: 20,
      total: 0,
      refreshTimer: null
    }
  },
  created() {
    this.loadTasks()
    // 智能轮询：有转码中任务时 5 秒刷，否则 15 秒
    this.startSmartRefresh()
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    startSmartRefresh() {
      if (this.refreshTimer) clearInterval(this.refreshTimer)
      const hasProcessing = this.tasks.some(t => t.status === 1)
      const interval = hasProcessing ? 5000 : 15000
      this.refreshTimer = setInterval(() => {
        this.loadTasks(true).then(() => {
          // 动态调整轮询频率
          const nowProcessing = this.tasks.some(t => t.status === 1)
          const curInterval = nowProcessing ? 5000 : 15000
          if (curInterval !== interval) this.startSmartRefresh()
        })
      }, interval)
    },
    async loadTasks(silent = false) {
      if (!silent) this.loading = true
      try {
        const res = await axios.post('/transcode/list', {
          status: this.filterStatus,
          page: this.currentPage,
          pageSize: this.pageSize
        })
        const data = res?.data?.data || res?.data || res
        this.tasks = data.records || []
        this.total = data.total || 0
      } catch (e) {
        if (!silent) this.$message.error('加载失败: ' + (e.message || ''))
      } finally {
        this.loading = false
      }
    },
    async retryTask(row) {
      try {
        await this.$confirm(`确定重试任务 #${row.id}（${row.sourceFileName}）？`, '重试确认', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          customClass: 'styled-confirm'
        })
        await axios.post('/transcode/retry', { taskId: row.id })
        this.$message.success('已重新加入队列')
        this.loadTasks()
      } catch (e) {
        if (e !== 'cancel') this.$message.error('重试失败: ' + (e.message || ''))
      }
    },
    statusText(s) {
      return { 0: '待处理', 1: '转码中', 2: '成功', 3: '失败' }[s] || '未知'
    },
    statusTag(s) {
      return { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger' }[s] || 'info'
    },
    formatTime(t) {
      if (!t) return '-'
      return t.replace('T', ' ').substring(0, 19)
    }
  }
}
</script>

<style scoped>
.transcode-admin {
  padding: 24px;
}
.page-header {
  margin-bottom: 20px;
}
.page-header h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  color: #303133;
}
.page-header .subtitle {
  margin: 0;
  font-size: 13px;
  color: #909399;
}
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}
.task-count {
  font-size: 13px;
  color: #909399;
}
.pagination-wrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>

<style>
.styled-confirm {
  border-radius: 12px !important;
  overflow: hidden;
  width: 420px !important;
}
.styled-confirm .el-message-box__header {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
}
.styled-confirm .el-message-box__title {
  font-size: 16px;
  font-weight: 600;
}
.styled-confirm .el-message-box__content {
  padding: 24px 20px;
  font-size: 14px;
  color: #374151;
}
.styled-confirm .el-message-box__btns {
  padding: 12px 20px 16px;
  border-top: 1px solid #f3f4f6;
}
.styled-confirm .el-button {
  border-radius: 9999px;
  padding: 8px 20px;
}
.styled-confirm .el-button--primary {
  background: #2563eb;
  border-color: #2563eb;
}
.styled-confirm .el-button--primary:hover {
  background: #1d4ed8;
  border-color: #1d4ed8;
}
</style>
